
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { UserT } from '@/core/types/users.types'
import { CompanyT } from '@/core/types/companies.types'
import { DeliveryCityT } from '@/core/types/delivery.types'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { delivery } from '@/core/realisations/delivery.realisation'

// constants
import {
    DELIVERY_STATUSES_OPTIONS,
    TRANSPORT_TYPES_OPTIONS,
    ORIGIN_OPTIONS,
} from '@/core/constants/delivery.constants'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['resetted', 'search'],
    setup(props, { emit }) {
        const state = reactive({
            company_id: null as number | null,
            user_id: null as number | null,
            city_id: null as number | null,
            status_id: null as number | null,
            company_name: null as string | null,
            company_bin: null as string | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
            invoiceNumber: null as string | null,
            containerName: null as string | null,
            transportType: null as string | null,
            origin: null as number | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            users: new DataState<UserT>({}),
            cities: new DataState<DeliveryCityT>({}),
        })

        const reset = () => {
            if (props.loading) return

            state.company_id = null
            state.user_id = null
            state.city_id = null
            state.status_id = null
            state.company_name = null
            state.company_bin = null
            state.createdAtFrom = null
            state.createdAtTo = null
            state.invoiceNumber = null
            state.containerName = null
            state.transportType = null
            state.origin = null

            emit('resetted')
        }

        const search = () => {
            if (props.loading) return

            emit('search', {
                ...state,
            })
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, isExtended: 0, statusId: 2 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const loadUsers = async (page: number, search?: string) => {
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const data = await users.getList({
                    page,
                    email: search,
                })

                data_state.users.list = data.data

                data_state.users.pagination.total_pages = data.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const loadMoreUsers = async (page: number) => {
            if (page > data_state.users.pagination.total_pages) return
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const response = await users.getList({ page })
                data_state.users.list = data_state.users.list.concat(response.data)

                data_state.users.pagination.total_pages = response.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const searchUsers = (search_query: string) => {
            loadUsers(1, search_query)
        }

        const unshiftFindedUser = async (user: UserT) => {
            await loadUsers(data_state.users.pagination.current_page)
            data_state.users.list.unshift(user)
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true
                const response = await delivery.getCities(page, search)
                data_state.cities.list = response.data

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await delivery.getCities(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCities = (search_query: string) => {
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: DeliveryCityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadUsers(1)
            loadCities(1)
        })

        return {
            state,
            data_state,
            reset,
            search,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            loadUsers,
            loadMoreUsers,
            searchUsers,
            unshiftFindedUser,
            //
            loadCities,
            loadMoreCities,
            searchCities,
            unshiftFindedCity,
            //
            DELIVERY_STATUSES_OPTIONS,
            TRANSPORT_TYPES_OPTIONS,
            ORIGIN_OPTIONS,
        }
    },
})
